import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as _ from 'lodash';
import createRelationsText from './AgencyRegistrationApplicationRelationText';
import {
  AGENCY_REGISTRATION_APPLICATION_STATUS,
  ONE_MASTER_FLAG,
} from '../utilities/constants';
import { getAgencyRegistrationApplicationStatus } from '../utilities/constantsUtil';
import { isNullOrUndefinedOrEmptyStr } from '../utilities/stringUtil';
import AgencyRegistrationApplicationHistoryText from './AgencyRegistrationApplicationHistoryText';
import {
  PreviewFormText,
  PreviewFormTextChild,
  PreviewFormTextChildDifference,
  PreviewFormTextOpen,
} from './PreviewForm';
import { FormElement, FormListItem } from './CommonFormComponents';
import PrintUtility from '../utilities/printUtility';
import { DownloadFileErrorDialog } from './modals/DownloadFileErrorDialog';
import { getLabelPersonalCompanyType } from '../utilities/partnerCompanyUtil';
import { messageUtility } from '../utilities/messageUtility';

const AgencyRegistrationApplicationPreview = ({
  formValues,
  downloadFile,
  moveDetailPage,
  isVisibleOtherAgenciesCount = true, // 代行実施中の会社数
  isOrigin = false, // 自社の代行元一覧からアクセス
  footerComponent = null,
  canDownloadMemorandumFile,
  isDifferent,
}) => {
  const [visibleDownloadErrorDialog, setVisibleDownloadErrorDialog] = useState(
    false
  );

  const isFileDeleted = useMemo(() => !!formValues.file_deleted_flag, [
    formValues,
  ]);

  const [isLoadingData, setIsLoadingData] = useState(false);
  const isRequestingFlag = useRef(false)

  useEffect(
    () => {
      setIsLoadingData(!formValues.id);
    },
    [formValues]
  );

  const isPersonalCompany =
    formValues.company_agent_to_one_master_flag === ONE_MASTER_FLAG.personal.id;

  const getLabelOneMaster = () => {
    if (isLoadingData) {
      return null;
    }
    return isPersonalCompany
      ? ONE_MASTER_FLAG.personal.name
      : `${ONE_MASTER_FLAG.corporation.name}（法人番号：${
          formValues.company_agent_to_company_number
        }）`;
  };

  const getKokuzeiName = () => {
    if (isLoadingData) {
      return null;
    }
    return isPersonalCompany
      ? '個人事業主のため国税庁法人登録情報はありません。'
      : isNullOrUndefinedOrEmptyStr(formValues.kokuzei_company_number)
      ? '登録情報なし'
      : formValues.kokuzei_name;
  };

  const downloadFileManual = useCallback(async () => {
    if (isRequestingFlag.current) {
      return;
    }
    isRequestingFlag.current = true;
    await PrintUtility.downloadStaticFilePDF(
      'agencyRegistrationManual',
      '代行登録説明資料（機能概要・覚書記入方法）.pdf',
      setVisibleDownloadErrorDialog
    );
    isRequestingFlag.current = false;
  }, []);

  return (
    <ul className='form-list form-list-default'>
      {![
        AGENCY_REGISTRATION_APPLICATION_STATUS.APPROVED.ID,
        AGENCY_REGISTRATION_APPLICATION_STATUS.REJECTED.ID,
        AGENCY_REGISTRATION_APPLICATION_STATUS.EXPIRED.ID,
      ].includes(formValues.status_no) ? (
        <PreviewFormText
          label={'ステータス'}
          values={[
            getAgencyRegistrationApplicationStatus(formValues.status_no),
          ]}
        />
      ) : (
        <React.Fragment>
          <PreviewFormTextOpen
            label={'ステータス'}
            values={[
              getAgencyRegistrationApplicationStatus(formValues.status_no),
            ]}
          />
          <PreviewFormTextChild
            label={
              formValues.status_no ===
              AGENCY_REGISTRATION_APPLICATION_STATUS.APPROVED.ID
                ? '承認理由'
                : formValues.status_no ===
                  AGENCY_REGISTRATION_APPLICATION_STATUS.REJECTED.ID
                ? '却下理由'
                : '失効理由'
            }
            values={[
              formValues.status_no ===
              AGENCY_REGISTRATION_APPLICATION_STATUS.APPROVED.ID
                ? formValues.approved_reason
                : formValues.rejected_reason,
            ]}
            preWrap={true}
          />
        </React.Fragment>
      )}
      <PreviewFormText label={'申請No.'} values={[formValues.id]} />
      <PreviewFormTextChildDifference
        label={' '}
        leftValue={'Buildee登録情報'}
        rightValue={'国税庁法人登録情報'}
        header
      />
      <PreviewFormTextChildDifference
        label={'会社名'}
        leftValue={formValues.company_agent_to_name}
        rightValue={getKokuzeiName()}
        isDifferent={isDifferent.includes('name')}
      />
      <PreviewFormTextChildDifference
        label={'法人種別'}
        leftValue={getLabelOneMaster()}
        rightValue={
          isNullOrUndefinedOrEmptyStr(formValues.kokuzei_company_number)
            ? ''
            : `${ONE_MASTER_FLAG.corporation.name}（法人番号：${
                formValues.kokuzei_company_number
              }）`
        }
      />
      {!isLoadingData && isPersonalCompany && (
        <PreviewFormTextChildDifference
          label={messageUtility.getMessage('I-000-186')}
          leftValue={getLabelPersonalCompanyType(
            formValues.company_agent_to_one_owner_flag
          )}
        />
      )}
      {/* <PreviewFormTextChild
        label={'郵便番号'}
        values={[formValues.company_agent_to_postal_code]}
      /> */}
      <PreviewFormTextChildDifference
        label={'住所'}
        leftValue={formValues.company_agent_to_address}
        rightValue={formValues.kokuzei_address}
        isDifferent={isDifferent.includes('address')}
      />
      <PreviewFormTextChildDifference
        label={'電話番号'}
        leftValue={formValues.company_agent_to_tel}
        rightValue={
          !isPersonalCompany &&
          !isNullOrUndefinedOrEmptyStr(formValues.kokuzei_company_number) &&
          '-'
        }
      />
      {isVisibleOtherAgenciesCount && (
        <PreviewFormTextChildDifference
          label={'代行実施中の会社数（自社を除く）'}
          leftValue={
            _.isNil(formValues.other_agencies_count)
              ? ''
              : formValues.other_agencies_count
          }
          rightValue={
            !isPersonalCompany &&
            !isNullOrUndefinedOrEmptyStr(formValues.kokuzei_company_number) &&
            '-'
          }
        />
      )}
      {!_.isEmpty(isDifferent) && (
        <FormListItem
          label={' '}
          className='form-list__item form-list__item--child'
          style={{
            backgroundColor: 'initial',
            borderBottom: '1px dashed #e0e0e0',
            padding: '12px 16px',
          }}
        >
          <FormElement>
            <span
              className='AgencyRegistrationApplicationForm__original-filename-info-attention'
              style={{ fontSize: 12 }}
            >
              Buildeeと国税庁の登録情報に差異があります。代行登録説明資料（機能概要・覚書記入方法）の「申請前確認事項」をご確認ください。
              <br />
              {!isOrigin && 'ひとつ前のページで'}
              ダウンロードしていない場合は
              <span
                className='clickable-text'
                onClick={downloadFileManual}
              >
                こちら
              </span>
              からダウンロードしてください。
            </span>
          </FormElement>
        </FormListItem>
      )}
      <DownloadFileErrorDialog
        visible={visibleDownloadErrorDialog}
        onClose={() => setVisibleDownloadErrorDialog(false)}
      />
      <PreviewFormText
        label={'代行担当者'}
        values={_.map(
          formValues.user_agency_registration_permissions,
          user => user.user_name
        )}
      />
      {canDownloadMemorandumFile && !isFileDeleted ? (
        <PreviewFormText
          label={'覚書の添付'}
          values={[
            <div key={1} className={'clickable-text'} onClick={downloadFile}>
              {formValues.original_filename}
            </div>,
          ]}
        />
      ) : (
        <FormListItem label='覚書の添付'>
          <FormElement
            info={
              isFileDeleted
                ? ''
                : '※覚書は、ステータスが承認の時のみダウンロード可能です。'
            }
          >
            {formValues.original_filename}
            {isFileDeleted && '（管理者により削除済み）'}
          </FormElement>
        </FormListItem>
      )}
      <PreviewFormText
        label={'履歴'}
        values={[
          <AgencyRegistrationApplicationHistoryText
            key={1}
            title={'登録日時'}
            date={formValues.registered_at}
          />,
          <AgencyRegistrationApplicationHistoryText
            key={2}
            title={'申請日時'}
            date={formValues.applied_at}
          />,
          <AgencyRegistrationApplicationHistoryText
            key={3}
            title={'承認日時'}
            date={formValues.approved_at}
          />,
          <AgencyRegistrationApplicationHistoryText
            key={4}
            title={'完了日時'}
            date={formValues.released_at}
          />,
        ]}
      />
      <PreviewFormText
        label={'関連する申請'}
        values={createRelationsText(
          formValues.id,
          formValues.related_applications,
          moveDetailPage
        )}
      />
      {footerComponent && footerComponent}
    </ul>
  );
};

export default AgencyRegistrationApplicationPreview;



// WEBPACK FOOTER //
// ./src/components/AgencyRegistrationApplicationPreview.jsx